import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { ArrowLeft, Lock, LockOpen } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PeriodLocksPage() {
  const navigate = useNavigate();
  const [locks, setLocks] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [lockDate, setLockDate] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('erp_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchLocks = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/accounting/period-locks`, { headers });
      setLocks(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load period locks');
    }
  };

  useEffect(() => { fetchLocks(); /* eslint-disable-next-line */ }, []);

  const handleSave = async () => {
    if (!lockDate) return toast.error('Pick a date');
    try {
      setSaving(true);
      await axios.post(`${API_URL}/api/accounting/period-locks`, { locked_through_date: lockDate, note }, { headers });
      toast.success(`Period locked through ${lockDate}`);
      setShowAdd(false);
      setLockDate(''); setNote('');
      fetchLocks();
    } catch (e) {
      toast.error(e.response?.data?.message || e.response?.data?.detail || 'Failed to lock');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlock = async (lock) => {
    if (!window.confirm(`Unlock period locked through ${lock.locked_through_date}?\n\nUsers will be able to create, edit, and delete transactions on or before that date again.`)) return;
    try {
      await axios.delete(`${API_URL}/api/accounting/period-locks/${lock.id}`, { headers });
      toast.success('Period unlocked');
      fetchLocks();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to unlock');
    }
  };

  const latest = locks[0];

  return (
    <div className="space-y-8 p-6 lg:p-8" data-testid="period-locks-page">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-2" data-testid="period-back">
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold flex items-center gap-2">
              <Lock size={32} className="text-primary" /> Period Locking
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Close accounting periods so historical transactions become read-only.
            </p>
          </div>
          <Button onClick={() => setShowAdd(true)} data-testid="add-lock-btn">
            <Lock size={14} className="mr-1" /> Lock New Period
          </Button>
        </div>
      </motion.div>

      {latest && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Lock size={28} className="text-primary" />
              <div>
                <div className="text-sm text-muted-foreground">Currently locked through</div>
                <div className="font-mono text-2xl font-semibold">{latest.locked_through_date}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Locked by {latest.locked_by_name || latest.locked_by} on {latest.locked_at?.replace('T', ' ').slice(0, 19)}
                  {latest.note ? ` · "${latest.note}"` : ''}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Lock History</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Locked Through</TableHead>
                <TableHead>Locked By</TableHead>
                <TableHead>Locked At</TableHead>
                <TableHead>Note</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {locks.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground">No period locks set yet.</TableCell></TableRow>
              )}
              {locks.map((lock) => (
                <TableRow key={lock.id} data-testid={`lock-row-${lock.id}`}>
                  <TableCell className="font-mono">{lock.locked_through_date}</TableCell>
                  <TableCell>{lock.locked_by_name || lock.locked_by}</TableCell>
                  <TableCell className="text-xs">{lock.locked_at?.replace('T', ' ').slice(0, 19)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{lock.note || '—'}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline" size="sm"
                      onClick={() => handleUnlock(lock)}
                      className="text-amber-600 border-amber-200 hover:bg-amber-50"
                      data-testid={`unlock-${lock.id}`}
                    >
                      <LockOpen size={14} className="mr-1" /> Unlock
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lock Accounting Period</DialogTitle>
            <DialogDescription>
              All transactions on or before the chosen date will become read-only — no creation, editing, or deletion.
              Approved vouchers will not be re-postable into the locked range.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Lock through date *</Label>
              <Input type="date" value={lockDate} onChange={(e) => setLockDate(e.target.value)} data-testid="lock-date-input" />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Q4 FY24 close — audit signed off" data-testid="lock-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="lock-save-btn">
              {saving ? 'Locking…' : 'Lock Period'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
